import classNames from 'classnames';
import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { GridWithoutPagination } from 'components/common-form/builder/form/node/field/Grid/GridWithoutPagination';
import { NonRelevantCell } from 'components/common-form/builder/form/node/field/NonRelevantField/NonRelevantCell';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { useSPIntl } from 'hooks/useIntl';
import type { GridData, GridMetadata } from 'models/forms/fields';
import { ScheduleModalFieldMetadata } from 'models/forms/fields/ScheduleModalFieldMetadata';
import { FC, useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { ProductNode } from 'state/product-description/model';
import styled from 'styled-components';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';

export const ScheduleFieldLabel = styled.div`
  cursor: pointer;

  &:hover {
    background-color: var(--bs-bg-lvl2);
  }
`;

export const ScheduleModal = styled(Modal)`
  @media (min-width: 1440px) {
    --bs-modal-width: 1300px;
  }
`;

export const ScheduleModalBody = styled(ModalBody)`
  padding-bottom: 0;
`;

export const BatchScheduleModalField: FC<ProductNodeBuilderProps> = ({
  tabId,
  node,
  tabIndex,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  batchTabId,
  batchColumnIndex = 0,
}) => {
  const { formatMessage } = useSPIntl();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { gridFieldId } = useFieldMetadata<ScheduleModalFieldMetadata>(node.id)!;
  const {
    headers,
    valuesMetadata: rowsMetadata,
    translationKey,
    hasRowIndexColumn,
    isCustomized,
  } = useFieldMetadata<GridMetadata>(gridFieldId)!;
  const patchedFieldClassName = usePatchedFieldHighlight(node.id);
  const [gridData] = useData<GridData>(gridFieldId);
  const nbRows = gridData.length;

  const nbColumns = headers?.length ?? 0;

  const toggle = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const icon = useMemo<string>(() => {
    return isCustomized ? 'edit_calendar' : 'calendar_month';
  }, [isCustomized]);

  const topBarNode = useMemo<ProductNode | undefined>(
    () => node.children?.find(n => n.id === `${gridFieldId}TopBar`),
    [node, gridFieldId],
  );

  const footerNode = useMemo<ProductNode | undefined>(
    () => node.children?.find(n => n.id === `${gridFieldId}Footer`),
    [node, gridFieldId],
  );

  if (headers === undefined || nbRows === 0 || nbColumns === 0) {
    return <NonRelevantCell />;
  }

  return (
    <>
      <BlockOrientationFormGroup
        inputId={node.id}
        label={`form.${translationKey}` as any}
        className={patchedFieldClassName}
      >
        <ScheduleFieldLabel
          className={classNames(
            'form-control',
            'd-flex flex-row align-items-center justify-content-between h-100 pe-0',
          )}
          onClick={toggle}
        >
          <div className="user-select-none">
            <FormattedMessage id={`form.${gridFieldId}.periods` as any} values={{ nbRows: nbRows }} />
          </div>
          <div className="ms-auto">
            {isCustomized && (
              <span className="badge badge-sm badge-outline-info rounded-pill me-1">
                <FormattedMessage id={`form.${gridFieldId}.manual` as any} />
              </span>
            )}
            <button
              className={classNames('btn btn-icon', isCustomized ? 'btn-flat-info' : 'btn-flat-secondary')}
              onClick={toggle}
            >
              <i className="icon icon-sm">{icon}</i>
            </button>
          </div>
        </ScheduleFieldLabel>
      </BlockOrientationFormGroup>
      <ScheduleModal backdrop="static" data-e2e="modal" show={isModalOpen} size="xl" keyboard={false} onHide={toggle}>
        <ModalHeader className="pb-2" closeButton>
          <ModalTitle as="h3">
            <span className="d-flex align-items-center">
              <FormattedMessage id={`form.${gridFieldId}.title` as any} values={{ quoteIndex: batchColumnIndex + 1 }} />
              {isCustomized && (
                <span className="badge badge-lg badge-outline-info rounded-pill ms-3">
                  <FormattedMessage id={`form.${gridFieldId}.manual` as any} />
                </span>
              )}
            </span>
          </ModalTitle>
        </ModalHeader>
        <ScheduleModalBody>
          <div className="d-flex flex-column position-relative">
            {topBarNode && (
              <RenderFormNodeComponent
                key={topBarNode.id}
                tabId={tabId}
                level={1}
                workspaceType="newBatch"
                quoteWorkspaceVersion={quoteWorkspaceVersion}
                node={topBarNode}
                mustForceAdvancedMode={mustForceAdvancedMode}
                batchWorkspaceVersion={batchWorkspaceVersion}
              />
            )}
            <GridWithoutPagination
              id={gridFieldId}
              headers={headers}
              rowsMetadata={rowsMetadata}
              hasRowIndexColumn={hasRowIndexColumn}
              nbColumns={nbColumns}
              tabIndex={tabIndex}
            ></GridWithoutPagination>
          </div>
        </ScheduleModalBody>
        <ModalFooter>
          <div className="w-100 d-flex align-items-center justify-content-between">
            {footerNode && (
              <RenderFormNodeComponent
                key={footerNode.id}
                tabId={tabId}
                level={1}
                workspaceType="newBatch"
                quoteWorkspaceVersion={quoteWorkspaceVersion}
                node={footerNode}
                mustForceAdvancedMode={mustForceAdvancedMode}
                batchWorkspaceVersion={batchWorkspaceVersion}
                batchTabId={batchTabId}
              />
            )}
            <button className="btn btn-lg btn-primary" onClick={toggle}>
              {formatMessage({ id: 'ok', defaultMessage: 'OK' })}
            </button>
          </div>
        </ModalFooter>
      </ScheduleModal>
    </>
  );
};

BatchScheduleModalField.displayName = 'BatchScheduleModalField';
